<script lang="ts">
	//Svelte
	import { afterUpdate, createEventDispatcher, onMount } from 'svelte';
	import { location } from 'svelte-spa-router';
	//Services
	import { mobile } from 'src/services/mobile-rework';
	//Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { extended_dashboards } from 'src/stores/features/dashboards';
	import { is_mobile } from 'src/stores/ui/viewport';
	//Helpers
	import { format } from 'svelte-i18n';
	import {
		setWizardSteps,
		updateWizardStepOnWizardItem,
		updateWizardStepOnPrevNext,
		resetWizardItems,
		scrollToElement,
	} from '../../Lists/list-helpers';
	import { requestAddDashItem, canAddDashboardWidget } from '../dash-helpers';
	import { requestAddDashEnergyWidgets } from '../dash-energy-helpers';
	import { isDashEnergyWidgetType } from '../dash-energy-types';
	import { getCameraSelectionLimit, getDashWidgetSelectListRefKey } from '../dash-camera-helpers';
	import type {
		DashWidgetEditType,
		DashWidgetTypes,
		DashWizardInitialStep,
		ListRefType,
	} from '../dash-types';
	//Design System Components
	import {
		Button,
		Modal,
		Wizard,
		WizardItem,
		Container,
		WizardContainer,
		WizardContent,
	} from '@Shelly/shelly-design-system';
	//Components
	import DashWidgetSelectDashStep from './Steps/DashWidgetSelectDashStep.svelte';
	import DashWidgetItemsSelectStep from './Steps/DashWidgetItemsSelectStep.svelte';
	import DashWidgetTypeStep from './Steps/DashWidgetTypeStep.svelte';
	import DashWidgetReorderStep from './Steps/DashWidgetReorderStep.svelte';
	import DashWidgetGroupStep from './Steps/DashWidgetGroupStep.svelte';
	import DuplicateItemModal from './Components/DuplicateItemModal.svelte';
	import DashWidgetWeatherInfoStep from './Steps/DashWidgetWeatherInfoStep.svelte';
	import DashEnergyWidgetConfigStep from './Steps/DashEnergyWidgetConfigStep.svelte';
	import DashEnergyInfoSelectStep from './Steps/DashEnergyInfoSelectStep.svelte';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import DashWidgetLimitReached from './Components/DashWidgetLimitReached.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	export let editItem: DashWidgetEditType;
	export let showAddEditModal: boolean;
	export let items;
	export let updatedItem;
	export let hideSelectDash = false;
	export let isAddWizard = false;
	export let energyOnly = false;
	export let initialStep: DashWizardInitialStep | undefined;

	let hasWeatherWidgetCard = false;
	let showDuplicateItemModal: boolean = false;
	let duplicateItemModalNotification: string = '';
	let wizardItemsContainerRef: HTMLElement = null;
	let modalRef: Modal = null;
	let scrolledToView = false;
	let allItems = [...items];
	let waitingForResponse = false;
	let allTypeRefs: ListRefType = {};
	let allItemsRefs: ListRefType = {};
	let allDashRefs: ListRefType = {};
	let editItemSnapshot = '';
	let showExitConfirmationModal = false;
	let skipSelectDashMobile = $is_mobile && ($edit || (isAddWizard && hideSelectDash));

	const buildWizardConfig = (format: typeof $format) => {
		const dashWizardFlowDefaultSteps = [
			...(skipSelectDashMobile
				? []
				: [{ component: DashWidgetSelectDashStep, props: { allDashRefs } }]),
			{ component: DashWidgetTypeStep, props: { allTypeRefs } },
		];
		const dashWizardFlowSelectList = [
			...dashWizardFlowDefaultSteps,
			{ component: DashWidgetItemsSelectStep, props: { allItemsRefs } },
			{ component: DashWidgetReorderStep },
			{ component: DashWidgetGroupStep },
		];

		const dashWizardFlowEnergyConfig = [
			...dashWizardFlowDefaultSteps,
			{ component: DashEnergyWidgetConfigStep },
			{ component: DashWidgetGroupStep },
		];
		const dashWizardFlowEnergyInfo = [
			...dashWizardFlowDefaultSteps,
			{ component: DashEnergyInfoSelectStep, props: { allItemsRefs } },
			{ component: DashEnergyWidgetConfigStep },
			{ component: DashWidgetGroupStep },
		];
		const dashWizardFlowEnergyRoom = [
			...dashWizardFlowDefaultSteps,
			{ component: DashWidgetItemsSelectStep, props: { allItemsRefs } },
			{ component: DashEnergyWidgetConfigStep },
			{ component: DashWidgetGroupStep },
		];
		const dashWizardFlowType: { [key in DashWidgetTypes | 'default']: {}[] } = {
			default: dashWizardFlowDefaultSteps,
			dashWeather: [
				...dashWizardFlowDefaultSteps,
				{ component: DashWidgetWeatherInfoStep, props: { allItemsRefs } },
				{ component: DashWidgetGroupStep },
			],
			dashAlarm: dashWizardFlowSelectList,
			dashControlDev: dashWizardFlowSelectList,
			dashParameterDev: dashWizardFlowSelectList,
			dashQuickDev: dashWizardFlowSelectList,
			dashCardDev: dashWizardFlowSelectList,
			dashCamera: dashWizardFlowSelectList,
			dashScene: dashWizardFlowSelectList,
			dashGroup: dashWizardFlowSelectList,
			dashRoom: dashWizardFlowSelectList,
			dashMedia: dashWizardFlowSelectList,
			dashThermostat: dashWizardFlowSelectList,
			dashChart: [...dashWizardFlowDefaultSteps, { component: DashWidgetGroupStep }],
			dashInfo: [...dashWizardFlowDefaultSteps, { component: DashWidgetGroupStep }],
			dashLight: dashWizardFlowSelectList,
			dashEnergyInfo: dashWizardFlowEnergyInfo,
			dashEnergyKpiLive: dashWizardFlowEnergyConfig,
			dashEnergyKpiPeriod: dashWizardFlowEnergyConfig,
			dashEnergyTopConsumers: dashWizardFlowEnergyConfig,
			dashEnergyConsumerMix: dashWizardFlowEnergyConfig,
			dashEnergyRoomChart: dashWizardFlowEnergyRoom,
			dashEnergyFlow: dashWizardFlowEnergyConfig,
			dashEnergyHeatmap: dashWizardFlowEnergyConfig,
			dashEnergyPeak: dashWizardFlowEnergyConfig,
			dashEnergyInsights: dashWizardFlowDefaultSteps,
			dashEnergyGeneration: dashWizardFlowEnergyConfig,
			dashEnergyPvDistribution: dashWizardFlowDefaultSteps,
			dashEnergyTariff: dashWizardFlowEnergyConfig,
			dashEnergyPhase: dashWizardFlowDefaultSteps,
			dashEnergyEnvironmental: dashWizardFlowDefaultSteps,
			dashEnergyDeviceTrend: dashWizardFlowEnergyConfig,
			dashEnergyRoomCharts: dashWizardFlowEnergyConfig,
			dashEnergyRoomDistribution: dashWizardFlowEnergyConfig,
			dashEnvironment: dashWizardFlowSelectList,
			dashPromo: dashWizardFlowDefaultSteps,
		};

		const wizardStepsDefaultSteps = [
			...(skipSelectDashMobile
				? []
				: [format('label_select_dashboard', { default: 'Select dashboard' })]),
			format('label_select_type', { default: 'Select Type' }),
		];

		const wizardStepsSelectListType = [
			...wizardStepsDefaultSteps,
			format('label_select_items', { default: 'Select items' }),
			format('label_reorder', { default: 'Reorder' }),
			format('label_widget_group', { default: 'Widget Group' }),
		];

		const wizardStepsEnergyConfig = [
			...wizardStepsDefaultSteps,
			format('label_dash_energy_widget_settings', { default: 'Energy settings' }),
			format('label_widget_group', { default: 'Widget Group' }),
		];
		const wizardStepsEnergyInfo = [
			...wizardStepsDefaultSteps,
			format('label_select_info_metrics', { default: 'Select information' }),
			format('label_dash_energy_widget_settings', { default: 'Energy settings' }),
			format('label_widget_group', { default: 'Widget Group' }),
		];
		const wizardStepsEnergyRoom = [
			...wizardStepsDefaultSteps,
			format('label_select_items', { default: 'Select items' }),
			format('label_dash_energy_widget_settings', { default: 'Energy settings' }),
			format('label_widget_group', { default: 'Widget Group' }),
		];
		const wizardStepsFlowType: { [key in DashWidgetTypes | 'default']: string[] } = {
			default: wizardStepsDefaultSteps,
			dashWeather: [
				...wizardStepsDefaultSteps,
				format('label_select_weather_items', { default: 'Weather info' }),
				format('label_widget_group', { default: 'Widget Group' }),
			],
			dashAlarm: wizardStepsSelectListType,
			dashControlDev: wizardStepsSelectListType,
			dashParameterDev: wizardStepsSelectListType,
			dashQuickDev: wizardStepsSelectListType,
			dashCardDev: wizardStepsSelectListType,
			dashCamera: wizardStepsSelectListType,
			dashScene: wizardStepsSelectListType,
			dashGroup: wizardStepsSelectListType,
			dashRoom: wizardStepsSelectListType,
			dashMedia: wizardStepsSelectListType,
			dashThermostat: wizardStepsSelectListType,
			dashChart: [
				...wizardStepsDefaultSteps,
				format('label_widget_group', { default: 'Widget Group' }),
			],
			dashInfo: [
				...wizardStepsDefaultSteps,
				format('label_widget_group', { default: 'Widget Group' }),
			],
			dashLight: wizardStepsSelectListType,
			dashEnergyInfo: wizardStepsEnergyInfo,
			dashEnergyKpiLive: wizardStepsEnergyConfig,
			dashEnergyKpiPeriod: wizardStepsEnergyConfig,
			dashEnergyTopConsumers: wizardStepsEnergyConfig,
			dashEnergyConsumerMix: wizardStepsEnergyConfig,
			dashEnergyRoomChart: wizardStepsEnergyRoom,
			dashEnergyFlow: wizardStepsEnergyConfig,
			dashEnergyHeatmap: wizardStepsEnergyConfig,
			dashEnergyPeak: wizardStepsEnergyConfig,
			dashEnergyInsights: wizardStepsDefaultSteps,
			dashEnergyGeneration: wizardStepsEnergyConfig,
			dashEnergyPvDistribution: wizardStepsDefaultSteps,
			dashEnergyTariff: wizardStepsEnergyConfig,
			dashEnergyPhase: wizardStepsDefaultSteps,
			dashEnergyEnvironmental: wizardStepsDefaultSteps,
			dashEnergyDeviceTrend: wizardStepsEnergyConfig,
			dashEnergyRoomCharts: wizardStepsEnergyConfig,
			dashEnergyRoomDistribution: wizardStepsEnergyConfig,
			dashEnvironment: wizardStepsSelectListType,
			dashPromo: wizardStepsDefaultSteps,
		};

		return {
			dashWizardFlowType,
			wizardStepsFlowType,
		};
	};

	let { dashWizardFlowType, wizardStepsFlowType } = buildWizardConfig($format);
	const initialActiveIndex = initialStep === 'select-items' ? (skipSelectDashMobile ? 1 : 2) : 0;
	let wizardItems = setWizardSteps(
		wizardStepsFlowType[editItem.widgetType || 'default'],
		initialActiveIndex,
	);
	let activeWizardStep = wizardItems[initialActiveIndex] || wizardItems[0];
	let disabledConditions = [];
	let isNextButtonDisabled = false;

	//#region Wizard Scroll to Functions
	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;
		const selectItemIndex = skipSelectDashMobile ? 1 : 2;
		const selectDashTypeIndex = skipSelectDashMobile ? 0 : 1;

		if (
			activeWizardStep.activeIndex === selectItemIndex &&
			selectedRef &&
			allItemsRefs[selectedRef.id] &&
			!scrolledToView
		) {
			wizardStepIndex = selectItemIndex;
			elementRef = allItemsRefs[selectedRef.id];
		} else if (
			!isEditFlow &&
			activeWizardStep.activeIndex === selectDashTypeIndex &&
			allTypeRefs[
				getDashWidgetSelectListRefKey(
					editItem.widgetType,
					editItem.cameraConfig?.variant,
					editItem.energyConfig?.heatmapVariant,
					editItem.energyConfig?.peakVariant,
				)
			] &&
			!scrolledToView
		) {
			wizardStepIndex = selectDashTypeIndex;
			elementRef =
				allTypeRefs[
					getDashWidgetSelectListRefKey(
						editItem.widgetType,
						editItem.cameraConfig?.variant,
						editItem.energyConfig?.heatmapVariant,
						editItem.energyConfig?.peakVariant,
					)
				];
		} else if (
			activeWizardStep.activeIndex === 0 &&
			allDashRefs[editItem.dashboardId] &&
			!scrolledToView
		) {
			wizardStepIndex = 0;
			elementRef = allDashRefs[editItem.dashboardId];
		}

		if (!elementRef) {
			// Scroll to top, when no card is selected
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};
	//#endregion Wizard Scroll to Functions

	//#startregion Wizard Save Functions
	function handleSaveWidgets() {
		let alreadyExistingItems = [];

		for (const selectItem of editItem.selectItems) {
			allItems.find((item) => {
				if (String(item.id) === String(selectItem.id) && item._type === selectItem.type) {
					alreadyExistingItems.push(item);
				}
			});
		}

		if (alreadyExistingItems.length > 0) {
			showDuplicateItemModal = true;
			updatedItem = alreadyExistingItems[alreadyExistingItems.length - 1];

			duplicateItemModalNotification = isEditFlow
				? $format('duplicate_item_modal_notification_on_edit', {
						default: `This widget already exists in the list of the dashboard. If you select it now, it will come to the position of the selected widget.`,
					})
				: $format('duplicate_item_modal_notification_on_add', {
						values: { number: alreadyExistingItems.length },
						default: `${alreadyExistingItems.length} of the selected devices is already in the dashboard. When you add it again it will go to the bottom of the dashboard list.`,
					});
		} else {
			handleAddEditFinish();
		}
	}

	const handleAddEditFinish = async () => {
		try {
			waitingForResponse = true;

			const saveWidget = isDashEnergyWidgetType(editItem.widgetType)
				? requestAddDashEnergyWidgets
				: requestAddDashItem;

			await saveWidget(editItem);
			dispatch(isEditFlow ? 'afterUpdateWidget' : 'afterAddWidget');
			if ($edit) edit.set(false);
			onCloseAddEditModal();
		} catch (error) {
			console.error(error);
		}
		waitingForResponse = false;
	};
	//#endregion Wizard Save Functions

	//#startregion Wizard prev/next Functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};
	//#endregion Wizard prev/next Functions

	//#startregion Wizard Modal Functions
	const clearData = () => {
		allItems = [];
		editItem = {
			...editItem,
			id: '',
			position: -1,
			widgetTitle: '',
			showWidgetTitle: true,
			widgetType: '',
			selectItems: [],
			cameraConfig: undefined,
			deviceCardConfig: undefined,
		};
		wizardItems = resetWizardItems(wizardItems);
		activeWizardStep = wizardItems[0];
	};

	const closeModal = (e: CustomEvent) => {
		if (showAddEditModal) {
			e.preventDefault();
			showAddEditModal = false;
		}
	};

	const onCloseAddEditModal = () => {
		showAddEditModal = false;
		editItemSnapshot = ''; // Clean snapshot for confirmation modal
		clearData();
		dispatch('close');
	};

	const onCloseAddEditModalCheckConfirmation = () => {
		// Compare current state to the snapshot taken at opening
		const hasChanged = editItemSnapshot !== JSON.stringify(editItem);

		if (hasChanged) {
			showExitConfirmationModal = true;
		} else {
			onCloseAddEditModal();
		}
	};

	const onCloseConfirmationModal = (e: CustomEvent) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	};

	const onSubmitConfirmationModal = (e: CustomEvent) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showAddEditModal = e.detail.showAddEditModal;
		onCloseAddEditModal();
	};
	//#endregion Wizard Modal Functions

	//#startregion Wizard Disabled Conditions Functions
	const getWizardDisabledConditions = (editItem: DashWidgetEditType) => {
		const selectedCameraCount = editItem.selectItems.length;
		const cameraSelectionLimit = getCameraSelectionLimit(editItem.cameraConfig?.variant);
		const isCameraSelectionInvalid =
			editItem.widgetType === 'dashCamera'
				? selectedCameraCount < 1 || selectedCameraCount > cameraSelectionLimit
				: selectedCameraCount <= 0;
		const disableConditionsWizardFlow = [
			...(skipSelectDashMobile ? [] : [!editItem.dashboardId]),
			editItem.widgetType === '',
			editItem.widgetType === '',
		];

		const disableConditionsWizardFlowSelectList = [
			...(skipSelectDashMobile ? [] : [!editItem.dashboardId]),
			editItem.widgetType === '',
			isCameraSelectionInvalid,
			isCameraSelectionInvalid,
			isCameraSelectionInvalid,
		];

		const disabledConditions: { [key in DashWidgetTypes | 'default']: boolean[] } = {
			default: [
				...(skipSelectDashMobile ? [] : [!editItem.dashboardId]),
				editItem.widgetType === '',
			],
			dashWeather: disableConditionsWizardFlow,
			dashAlarm: disableConditionsWizardFlowSelectList,
			dashControlDev: disableConditionsWizardFlowSelectList,
			dashParameterDev: disableConditionsWizardFlowSelectList,
			dashQuickDev: disableConditionsWizardFlowSelectList,
			dashCardDev: disableConditionsWizardFlowSelectList,
			dashCamera: disableConditionsWizardFlowSelectList,
			dashScene: disableConditionsWizardFlowSelectList,
			dashGroup: disableConditionsWizardFlowSelectList,
			dashRoom: disableConditionsWizardFlowSelectList,
			dashMedia: disableConditionsWizardFlowSelectList,
			dashThermostat: disableConditionsWizardFlowSelectList,
			dashChart: disableConditionsWizardFlow,
			dashInfo: disableConditionsWizardFlow,
			dashLight: disableConditionsWizardFlowSelectList,
			dashEnergyInfo: disableConditionsWizardFlowSelectList,
			dashEnergyKpiLive: disableConditionsWizardFlow,
			dashEnergyKpiPeriod: disableConditionsWizardFlow,
			dashEnergyTopConsumers: disableConditionsWizardFlow,
			dashEnergyConsumerMix: disableConditionsWizardFlow,
			dashEnergyRoomChart: disableConditionsWizardFlowSelectList,
			dashEnergyFlow: disableConditionsWizardFlow,
			dashEnergyHeatmap: disableConditionsWizardFlow,
			dashEnergyPeak: disableConditionsWizardFlow,
			dashEnergyInsights: [
				...(skipSelectDashMobile ? [] : [!editItem.dashboardId]),
				editItem.widgetType === '',
			],
			dashEnergyGeneration: disableConditionsWizardFlow,
			dashEnergyPvDistribution: disableConditionsWizardFlow,
			dashEnergyTariff: disableConditionsWizardFlow,
			dashEnergyPhase: disableConditionsWizardFlow,
			dashEnergyEnvironmental: disableConditionsWizardFlow,
			dashEnergyDeviceTrend: disableConditionsWizardFlow,
			dashEnergyRoomCharts: disableConditionsWizardFlow,
			dashEnergyRoomDistribution: disableConditionsWizardFlow,
			dashEnvironment: disableConditionsWizardFlowSelectList,
			dashPromo: disableConditionsWizardFlow,
		};

		return disabledConditions[editItem.widgetType || 'default'];
	};
	//#endregion Wizard Disabled Conditions Functions

	//#startregion Svelte Hooks
	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});

	afterUpdate(() => {
		disabledConditions = getWizardDisabledConditions(editItem);
		isNextButtonDisabled = disabledConditions[activeWizardStep.activeIndex];
	});
	//#endregion Svelte Hooks

	//#startregion Reactive variables
	$: dashdId = editItem.dashboardId || $location.split('/')[2];

	$: isEditFlow = !isAddWizard && Boolean(editItem?.id);

	$: showDashItems = canAddDashboardWidget(editItem.dashboardId, { isEdit: isEditFlow });

	$: if (dashdId) {
		// Determine if the weather widget card exists on the dashboard
		hasWeatherWidgetCard = $extended_dashboards[dashdId]?.some((card) => card._type === 'weather');
	}

	$: skipSelectDashMobile = $is_mobile && ($edit || (isAddWizard && hideSelectDash));

	$: (skipSelectDashMobile,
		({ dashWizardFlowType, wizardStepsFlowType } = buildWizardConfig($format)));

	$: wizardItems = setWizardSteps(
		wizardStepsFlowType[editItem.widgetType || 'default'],
		activeWizardStep.activeIndex,
	);

	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: previousButton = $format('button_back');
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');

	// scroll in to view reactive variable
	$: selectedRef = editItem.selectItems[0];
	// reset scrolled to view variable after step change
	$: (activeWizardStep.activeIndex, (scrolledToView = false));

	$: if (showAddEditModal && !editItemSnapshot) {
		editItemSnapshot = JSON.stringify(editItem);
	}
	//#endregion Reactive variables
</script>

<Modal
	on:close={onCloseAddEditModalCheckConfirmation}
	showModal={showAddEditModal}
	title={showDashItems
		? $format(isEditFlow ? 'label_process_edit_widget' : 'label_process_add_widget', {
				default: isEditFlow ? 'Edit widget' : `Add widget`,
			})
		: $format('modal_title_widgets_limit_reached', {
				default: 'Widgets Limit Reached',
			})}
	bind:this={modalRef}
	backdropClose={false}
	removeScrollPadding
	hideSlotToolbar={!showDashItems}
	hideSlotFooter={!showDashItems}
>
	<Wizard
		slot="toolbar"
		style={wizardItems.length === 1 || !showDashItems ? 'visibility: hidden' : ''}
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				disabled={disabledConditions[i - 1]}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<svelte:fragment slot="content">
		<Container padding="0" style={!showDashItems ? 'padding:24px' : ''}>
			{#if showDashItems}
				<!--
					An edit may open on a later step. Render only its current step so the design
					system's slide track cannot initialize at zero with the content off-screen.
				-->
				<WizardContainer
					activeStepIndex={isEditFlow ? 0 : activeWizardStep.activeIndex}
					adaptiveHeight
					bind:wizardItemsContainerRef
				>
					{#each isEditFlow ? [activeWizardStep] : wizardItems as step (step.activeIndex)}
						<WizardContent
							active={isEditFlow || activeWizardStep.activeIndex === step.activeIndex}
						>
							<!-- // TODO fix console warning component created with unknown prop -->
							<svelte:component
								this={dashWizardFlowType[editItem.widgetType || 'default'][
									step.activeIndex
								]?.component}
								bind:editItem
								{...step.activeIndex === 0 ? { hideSelectDash } : {}}
								{...step.activeIndex === (skipSelectDashMobile ? 0 : 1)
									? { energyOnly }
									: {}}
								{allDashRefs}
								{allTypeRefs}
								{allItemsRefs}
							/>
						</WizardContent>
					{/each}
				</WizardContainer>
			{:else}
				<DashWidgetLimitReached />
			{/if}
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeWizardStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isNextButtonDisabled}
			on:click={() => {
				if (isLastStepWizard) {
					handleSaveWidgets();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<DuplicateItemModal
	showModal={showDuplicateItemModal}
	{duplicateItemModalNotification}
	name={$format('modal_duplicate_title', { default: 'Duplicate widgets' })}
	on:click={handleAddEditFinish}
/>

<ExitConfirmationModal
	type={$format('the_alarm', { default: 'the alarm' })}
	{showExitConfirmationModal}
	{showAddEditModal}
	on:close={onCloseConfirmationModal}
	on:confirm={onSubmitConfirmationModal}
/>
